import api from '@/apis'
import { createLatestOnly } from '@/utils/latestOnly'
import { createUiSession } from '@/utils/uiSession'

// REQUEST OWNERSHIP — the session MUTATIONS compete for this: two logins and a logout all write
// the same exclusive resource, so the newest wins and a slow first login cannot overwrite the
// session a second one just established.
//
// `verifyToken` is NOT one of them. It is a READ, and a read does not CLAIM. It used to, and
// `App.vue` polls it every 10 seconds: one tick landing inside `await sso.signOut()` took the
// token from the logout, whose `finally` then saw `isLatest() === false` and skipped the ENTIRE
// local teardown — no RESET_AUTH_STATE, no /login, and the non-expiring "Logging out..." snackbar
// left on a still-authenticated tab. On a shared terminal the operator walks away logged in.
const session_request = createLatestOnly()

// SESSION IDENTITY — "is the session I read about still the session that is established?" This is
// what `verifyToken` actually needs, and it is a different question from "am I the newest request".
// Advanced by the two mutations that change WHICH session exists, so a verification that started
// under an old session cannot tear down a newer one.
const auth_session = createUiSession()
import router from '@/router'

export default {
  state: {
    status: false,
    method: null,
    user_info: {},
    token: null
  },
  mutations: {
    SET_AUTH_STATE(state, payload) {
      state.status = payload.status
      state.method = payload.method
      state.user_info = payload.user_info
      state.token = payload.token
      auth_session.transition()
    },
    RESET_AUTH_STATE(state) {
      state.status = false
      state.method = null
      state.user_info = {}
      state.token = null
      auth_session.transition()
    }
  },
  actions: {
    // Returns whether the session it checked is still established and still valid. App.vue used to
    // hand-roll this request inline (`this.$api.verifyToken(...)`) and guard nothing, so round 10's
    // fix here was true of the action and false of the app: a mount-time check answering after the
    // operator had logged in as someone else tore down the NEW session. There is one caller-visible
    // answer now, and no second copy of the request.
    async verifyToken({ commit, state }) {
      // Captured, not claimed — see the note above. Capturing is a READ of the session, so a
      // heartbeat tick takes nothing away from a logout or a login that is in flight.
      const sameSession = auth_session.current()
      try {
        if (state.status) {
          const response = await api.verifyToken({
            params: { token: state.token }
          })
          if (!response.data.success) throw Error(response.data.message)
        }
        // Verified, and still the session on screen.
        return sameSession()
      } catch (error) {
        // A stale check must not tear down a session that has since changed — a newer login
        // established it, or a logout already tore it down.
        if (!sameSession()) return false
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
        commit('RESET_AUTH_STATE')
        router.push({ path: '/login' })
        return false
      }
    },
    // SSO Login
    async authenticateUserBySSO({ commit, dispatch }, payload) {
      const isLatest = session_request()
      try {
        const response = await api.loginBySSO(payload)
        // A slow first login must not overwrite the session a later one already established.
        if (!isLatest()) return
        if (response.data.success) {
          commit('SET_AUTH_STATE', {
            status: true,
            method: 'sso',
            user_info: response.data.data.user_info,
            token: response.data.data.token
          })
          dispatch('initTicketingSystem')
          router.push({ path: '/dashboard' })
          commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Login Success', icon: 'check', timeout: '2000' })
        } else {
          throw Error(response.data.message)
        }
      } catch (error) {
        // Ownership applies to the FAILURE path too. A first login that rejects after a second one
        // has succeeded would otherwise overwrite the success message AND bounce the operator to
        // /login, tearing down a session that is perfectly valid.
        if (!isLatest()) return
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
        router.push({ path: '/login' })
      }
    },
    // Normal Login
    async authenticateUser({ commit, dispatch }, payload) {
      const { username, password } = payload
      // VALIDATED BEFORE THE TOKEN IS CLAIMED. Claiming first meant a submit with a blank field —
      // which sends nothing and establishes nothing — still superseded an in-flight logout, whose
      // `finally` then skipped the whole local teardown. That is the round-10 HIGH reached through
      // a different door, and it also let a blank submit discard a concurrent SUCCESSFUL login.
      if (username === null || password === null || username.trim() === '' || password.trim() === '') {
        commit('SET_SNACKBAR', { status: true, color: 'error', icon: 'fas fa-times', text: 'Please fill in username and password', timeout: 2000 })
      } else {
        const isLatest = session_request()
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Authenticating your identity...', loading: true })
        try {
          const response = await api.login({ params: payload })
          // A slow first login must not overwrite the session a later one already established.
          if (!isLatest()) return
          if (response.data.success) {
            commit('SET_AUTH_STATE', {
              status: true,
              method: 'normal',
              user_info: response.data.data.user_info,
              token: response.data.data.token
            })
            dispatch('initTicketingSystem')
            router.push({ path: '/dashboard' })
            commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Login Success', icon: 'check', timeout: '2000' })
          } else {
            commit('SET_SNACKBAR', { status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
          }
        } catch (error) {
          // Ownership applies to the FAILURE path too — see authenticateUserBySSO.
          if (!isLatest()) return
          commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
        }
      }
    },
    // `= {}` is load-bearing: src/apis/axios.js dispatches this with NO payload on a 401 or a
    // Network Error, and destructuring `undefined` threw a TypeError inside the interceptor —
    // so the app's only automatic session teardown never ran at all.
    async logoutUser({ commit, state }, { token = null, sso = null } = {}) {
      // RE-ENTRANCY GUARD, and it is what makes the `= {}` above safe. `api.logout` is fired
      // unawaited; if it fails with a Network Error or a 401 the response interceptor dispatches
      // this action AGAIN, which fires it again — measured at 61 POSTs and still climbing before
      // this line existed. That loop was previously unreachable only because destructuring
      // `undefined` threw during parameter binding, i.e. the crash was holding it shut.
      if (!state.status) return
      // Logout CLAIMS the token, which supersedes any login still in flight so a slow login cannot
      // log the operator back in. It must ALSO check it after its own await: `sso.signOut()` can
      // take long enough for the operator to log in again, and the newest session action wins in
      // BOTH orderings. (A logout cannot be suppressed by an OLDER login — it took the token first
      // — so gating it here does not make it suppressible in the unsafe direction.)
      const isLatest = session_request()
      // The teardown is gated on the SESSION, not on request ownership. Claiming still stops a slow
      // login from logging the operator back in, but `isLatest()` answered the wrong question here:
      // a login that claims the token and then FAILS (bad password, network error, or a blank form)
      // establishes nothing, yet made the logout skip its entire `finally` — old token alive, no
      // /login, on an authenticated-looking screen. `sameSession()` asks what actually matters:
      // has a new session been established since I started? Only then may this teardown stand down.
      const sameSession = auth_session.current()
      try {
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Logging out...', loading: true })
        // Fire-and-forget, but its rejection is swallowed HERE rather than escaping as an
        // unhandled rejection on every logout.
        Promise.resolve(api.logout({ params: { token: token ? token : state.token } })).catch(() => {})
        if (state.method === 'sso' && sso) await sso.signOut()
      } catch (error) {
        console.log('Logout error', error.message)
      } finally {
        if (sameSession()) {
          // The MESSAGE is display, so it follows request ownership as every other display does:
          // if a newer session action is speaking, this one keeps quiet. The TEARDOWN below does
          // not — it is not display, and it is the thing that must happen.
          if (isLatest()) commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Logout Success', icon: 'check', timeout: '2000' })
          commit('RESET_AUTH_STATE')
          router.push({ path: '/login' })
        }
      }
    }
  }
}
